import { PEER } from '../../constants'

import { isUnifiedPlan } from '../../sdpUtils/sdpSemantics'
import { runPipeline } from '../../sdpUtils/pipeline'
import { removeTrickleIce, removeBundling, changeMediaDirection, preventDtlsRoleChange } from '../../sdpUtils/handlers'

/**
 * Creates an SDP answer, given that a remote offer has been set.
 * @method createAnswer
 * @param  {RTCAnswerOptions} [options={}] Options used to customize the answer.
 * @param  {Object} [options.mediaDirections] Directions to use for media.
 * @param  {string} [options.mediaDirections.audio]
 * @param  {string} [options.mediaDirections.video]
 * @return {Promise} Resolves with the answer.
 */
export default function createAnswer (options = {}) {
  const { nativePeer, config, dtlsRole, log } = this
  log.info('Creating local answer.')

  // If using unified-plan, remove options.mediaDirections.
  // This is because directions are now set in transceivers.
  if (isUnifiedPlan(config.rtcConfig.sdpSemantics)) {
    delete options.mediaDirections
  }

  return new Promise((resolve, reject) => {
    nativePeer
      .createAnswer(options)
      .then(answer => {
        const sdpHandlers = []

        /*
         * Always include the `preventDtlsRoleChange` handler. This ensures
         *    that the SDP's DTLS role does not change during a renegotiation.
         */
        sdpHandlers.push(preventDtlsRoleChange)

        if (config.trickleIceMode === PEER.TRICKLE_ICE.NONE) {
          // Modify the answer to claim the Peer doesn't support trickle ICE.
          sdpHandlers.push(removeTrickleIce)
        }
        if (config.removeBundling) {
          // Modify the offer to remove media bundling
          sdpHandlers.push(removeBundling)
        }
        if (options.mediaDirections) {
          // Modify the answer to set media directions as desired.
          sdpHandlers.push(changeMediaDirection(options.mediaDirections))
        }
        if (sdpHandlers.length > 0) {
          // Run the SDP pipeline with only these handlers.
          answer.sdp = runPipeline(sdpHandlers, answer.sdp, {
            type: answer.type,
            endpoint: PEER.ENDPOINT.LOCAL,
            dtlsRole: dtlsRole
          })
        }

        log.info('Finished creating local answer.')
        resolve(answer)
      })
      .catch(reject)
  })
}
